import { useState, useEffect } from 'react';
import { supabase, GalleryItem } from '@/lib/supabase';

const CATEGORIES = ['반영구 문신', '두피 반영구 문신', '입술 반영구 문신', '아이라인 반영구 문신', '교육현장', '기타'];

interface GalleryFormData {
  title: string;
  category: string;
  image_url: string;
  description: string;
}

const emptyForm: GalleryFormData = { title: '', category: '반영구 문신', image_url: '', description: '' };

export default function AdminGalleryTab() {
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<GalleryFormData>(emptyForm);
  const [editId, setEditId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [filterCategory, setFilterCategory] = useState('전체');

  const fetchItems = async () => {
    setLoading(true);
    const { data } = await supabase.from('gallery_items').select('*').order('created_at', { ascending: false });
    setItems(data || []);
    setLoading(false);
  };

  useEffect(() => { fetchItems(); }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim() || !form.image_url.trim()) return;
    setSaving(true);
    if (editId !== null) {
      await supabase.from('gallery_items').update({ ...form, updated_at: new Date().toISOString() }).eq('id', editId);
    } else {
      await supabase.from('gallery_items').insert([form]);
    }
    setSaving(false);
    setForm(emptyForm);
    setEditId(null);
    setShowForm(false);
    fetchItems();
  };

  const handleEdit = (item: GalleryItem) => {
    setForm({ title: item.title, category: item.category, image_url: item.image_url, description: item.description || '' });
    setEditId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    await supabase.from('gallery_items').delete().eq('id', id);
    setDeleteConfirmId(null);
    fetchItems();
  };

  const allCategories = ['전체', ...CATEGORIES];
  const filtered = filterCategory === '전체' ? items : items.filter((i) => i.category === filterCategory);

  return (
    <div>
      {/* 상단 액션 바 */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex gap-1 flex-wrap">
          {allCategories.map((cat) => (
            <button key={cat} onClick={() => setFilterCategory(cat)}
              className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer transition-colors ${filterCategory === cat ? 'bg-[#1B3A6B] text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
            >
              {cat}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <p className="text-sm text-gray-500">총 <strong className="text-[#1B3A6B]">{items.length}</strong>개</p>
          <button
            onClick={() => { setShowForm(!showForm); setForm(emptyForm); setEditId(null); }}
            className="whitespace-nowrap flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#1B3A6B] text-white text-sm font-semibold cursor-pointer hover:bg-[#15305a] transition-colors"
          >
            <i className="ri-add-line" /> 이미지 추가
          </button>
        </div>
      </div>

      {/* 작성/수정 폼 */}
      {showForm && (
        <form onSubmit={handleSubmit} className="mb-6 bg-[#F4F6FA] rounded-xl p-6 border border-gray-200">
          <h3 className="text-sm font-bold text-[#1B3A6B] mb-4">{editId ? '이미지 수정' : '새 이미지 추가'}</h3>
          <div className="space-y-3">
            <select
              value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="text-sm border border-gray-200 rounded-md px-3 py-2 bg-white cursor-pointer focus:outline-none focus:border-[#1B3A6B]"
            >
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input
              type="text" placeholder="제목을 입력하세요" value={form.title} required
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full text-sm border border-gray-200 rounded-md px-4 py-2.5 focus:outline-none focus:border-[#1B3A6B] bg-white"
            />
            <input
              type="url" placeholder="이미지 URL을 입력하세요" value={form.image_url} required
              onChange={(e) => setForm({ ...form, image_url: e.target.value })}
              className="w-full text-sm border border-gray-200 rounded-md px-4 py-2.5 focus:outline-none focus:border-[#1B3A6B] bg-white"
            />
            {form.image_url && (
              <div className="w-32 h-24 rounded-lg overflow-hidden border border-gray-200">
                <img src={form.image_url} alt="미리보기" className="w-full h-full object-cover object-top" />
              </div>
            )}
            <textarea
              placeholder="설명 (선택)" value={form.description} rows={2} maxLength={200}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full text-sm border border-gray-200 rounded-md px-4 py-2.5 focus:outline-none focus:border-[#1B3A6B] bg-white resize-none"
            />
          </div>
          <div className="flex gap-2 mt-4">
            <button type="submit" disabled={saving}
              className="whitespace-nowrap px-5 py-2 rounded-md bg-[#1B3A6B] text-white text-sm font-semibold cursor-pointer hover:bg-[#15305a] transition-colors disabled:opacity-50"
            >
              {saving ? '저장 중...' : editId ? '수정 완료' : '등록'}
            </button>
            <button type="button" onClick={() => { setShowForm(false); setForm(emptyForm); setEditId(null); }}
              className="whitespace-nowrap px-5 py-2 rounded-md border border-gray-200 text-gray-600 text-sm font-semibold cursor-pointer hover:bg-gray-50 transition-colors"
            >
              취소
            </button>
          </div>
        </form>
      )}

      {/* 갤러리 그리드 */}
      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-[#1B3A6B] border-t-transparent rounded-full animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm">등록된 이미지가 없습니다.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((item) => (
            <div key={item.id} className="group relative rounded-xl overflow-hidden border border-gray-100 bg-white">
              <div className="w-full h-40">
                <img src={item.image_url} alt={item.title} className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105" />
              </div>
              <div className="p-3">
                <span className="text-xs bg-[#1B3A6B]/10 text-[#1B3A6B] font-semibold px-2 py-0.5 rounded-sm">{item.category}</span>
                <p className="text-gray-800 text-sm font-medium mt-1.5 truncate">{item.title}</p>
                {item.description && <p className="text-gray-400 text-xs mt-0.5 truncate">{item.description}</p>}
                <p className="text-gray-300 text-xs mt-1">{new Date(item.created_at).toLocaleDateString('ko-KR')}</p>
              </div>
              {/* 수정/삭제 버튼 */}
              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => handleEdit(item)}
                  className="w-7 h-7 flex items-center justify-center rounded-md bg-white/90 text-[#1B3A6B] cursor-pointer hover:bg-white transition-colors" title="수정"
                >
                  <i className="ri-edit-line text-sm" />
                </button>
                <button onClick={() => setDeleteConfirmId(item.id)}
                  className="w-7 h-7 flex items-center justify-center rounded-md bg-white/90 text-red-500 cursor-pointer hover:bg-white transition-colors" title="삭제"
                >
                  <i className="ri-delete-bin-line text-sm" />
                </button>
              </div>
              {deleteConfirmId === item.id && (
                <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-3 p-4">
                  <p className="text-white text-sm font-semibold text-center">정말 삭제하시겠습니까?</p>
                  <div className="flex gap-2">
                    <button onClick={() => handleDelete(item.id)}
                      className="whitespace-nowrap px-4 py-1.5 rounded-md bg-red-500 text-white text-xs font-semibold cursor-pointer hover:bg-red-600 transition-colors"
                    >삭제</button>
                    <button onClick={() => setDeleteConfirmId(null)}
                      className="whitespace-nowrap px-4 py-1.5 rounded-md bg-white text-gray-700 text-xs font-semibold cursor-pointer hover:bg-gray-100 transition-colors"
                    >취소</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
